import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Upload, Camera, X, Loader2, FileText, Crop, ZoomIn, ZoomOut, Move } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PanZoomCropper = ({ src, onConfirm, onSkip, uploading }) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const FRAME_SIZE = 280;

  const onImageLoad = useCallback((e) => {
    const img = e.target;
    const naturalW = img.naturalWidth;
    const naturalH = img.naturalHeight;
    // Fit image so it fills the frame (cover)
    const fillScale = Math.max(FRAME_SIZE / naturalW, FRAME_SIZE / naturalH);
    setImgSize({ w: naturalW, h: naturalH });
    setScale(fillScale);
    // Center
    setPosition({
      x: (FRAME_SIZE - naturalW * fillScale) / 2,
      y: (FRAME_SIZE - naturalH * fillScale) / 2,
    });
  }, []);

  const handleZoom = (delta) => {
    setScale(prev => {
      // Min = contain (full image visible), allow down to 50% of that
      const containScale = Math.min(FRAME_SIZE / imgSize.w, FRAME_SIZE / imgSize.h);
      const minScale = containScale * 0.5;
      const newScale = Math.max(minScale, Math.min(prev + delta, prev * 3));
      // Adjust position to zoom toward center
      const cx = FRAME_SIZE / 2;
      const cy = FRAME_SIZE / 2;
      setPosition(pos => ({
        x: cx - (cx - pos.x) * (newScale / prev),
        y: cy - (cy - pos.y) * (newScale / prev),
      }));
      return newScale;
    });
  };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    handleZoom(delta * scale);
  }, [scale, imgSize]);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.addEventListener('wheel', handleWheel, { passive: false });
      return () => el.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  // Mouse drag
  const onMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  const onMouseMove = useCallback((e) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  }, [dragging, dragStart]);
  const onMouseUp = () => setDragging(false);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      return () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
    }
  }, [dragging, onMouseMove]);

  // Touch drag + pinch zoom
  const touchRef = useRef({ startDist: 0, startScale: 1 });
  const onTouchStart = (e) => {
    if (e.touches.length === 1) {
      setDragging(true);
      setDragStart({ x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y });
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      touchRef.current = { startDist: Math.hypot(dx, dy), startScale: scale };
    }
  };
  const onTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 1 && dragging) {
      setPosition({ x: e.touches[0].clientX - dragStart.x, y: e.touches[0].clientY - dragStart.y });
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const containScale = Math.min(FRAME_SIZE / imgSize.w, FRAME_SIZE / imgSize.h);
      const minScale = containScale * 0.5;
      const newScale = Math.max(minScale, touchRef.current.startScale * (dist / touchRef.current.startDist));
      setScale(newScale);
    }
  };
  const onTouchEnd = () => setDragging(false);

  const handleConfirm = () => {
    const canvas = document.createElement('canvas');
    canvas.width = FRAME_SIZE * 2; // 2x for quality
    canvas.height = FRAME_SIZE * 2;
    const ctx = canvas.getContext('2d');
    const img = imgRef.current;
    if (!img) return;
    // Draw the image as it appears in the frame, at 2x resolution
    const ratio = 2;
    ctx.drawImage(
      img,
      position.x * ratio, position.y * ratio,
      img.naturalWidth * scale * ratio,
      img.naturalHeight * scale * ratio
    );
    canvas.toBlob((blob) => {
      if (blob) onConfirm(blob);
    }, 'image/jpeg', 0.9);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-[#6E5E52] flex items-center gap-1.5">
        <Move className="w-3.5 h-3.5" /> Deplacez et zoomez la photo pour l'ajuster au cadre
      </p>
      <div className="flex justify-center">
        <div
          ref={containerRef}
          className="relative overflow-hidden rounded-2xl border-2 border-[#D4A853] bg-black"
          style={{ width: FRAME_SIZE, height: FRAME_SIZE, cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none' }}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <img
            ref={imgRef}
            src={src}
            alt="Recadrage"
            onLoad={onImageLoad}
            draggable={false}
            style={{
              position: 'absolute',
              left: position.x,
              top: position.y,
              width: imgSize.w * scale,
              height: imgSize.h * scale,
              maxWidth: 'none',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
          {/* Grid overlay */}
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: 'linear-gradient(rgba(212,168,83,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(212,168,83,0.15) 1px, transparent 1px)',
            backgroundSize: `${FRAME_SIZE/3}px ${FRAME_SIZE/3}px`
          }} />
          {/* Corner indicators */}
          <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#D4A853] rounded-tl-lg pointer-events-none" />
          <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#D4A853] rounded-tr-lg pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#D4A853] rounded-bl-lg pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#D4A853] rounded-br-lg pointer-events-none" />
        </div>
      </div>
      {/* Zoom controls */}
      <div className="flex items-center justify-center gap-3">
        <button type="button" onClick={() => handleZoom(-0.1 * scale)}
          className="w-9 h-9 rounded-xl bg-[#F3EDE5] flex items-center justify-center hover:bg-[#E5DCD3] transition-colors"
          data-testid="zoom-out-btn">
          <ZoomOut className="w-4 h-4 text-[#2D241D]" />
        </button>
        <div className="w-32 h-1.5 bg-[#E5DCD3] rounded-full overflow-hidden">
          <div className="h-full bg-[#D4A853] rounded-full transition-all" style={{
            width: `${Math.min(100, Math.max(5, ((scale / (Math.min(FRAME_SIZE / imgSize.w, FRAME_SIZE / imgSize.h) || 1)) - 0.5) / 2.5 * 100))}%`
          }} />
        </div>
        <button type="button" onClick={() => handleZoom(0.1 * scale)}
          className="w-9 h-9 rounded-xl bg-[#F3EDE5] flex items-center justify-center hover:bg-[#E5DCD3] transition-colors"
          data-testid="zoom-in-btn">
          <ZoomIn className="w-4 h-4 text-[#2D241D]" />
        </button>
      </div>
      <div className="flex gap-2">
        <button onClick={onSkip} type="button"
          className="flex-1 py-2.5 rounded-xl border border-[#E5DCD3] text-sm text-[#6E5E52] hover:bg-[#F3EDE5] transition-colors"
          data-testid="skip-crop-btn">
          Annuler
        </button>
        <button onClick={handleConfirm} disabled={uploading} type="button"
          className="flex-1 py-2.5 rounded-xl bg-[#0A422D] text-white text-sm font-semibold hover:bg-[#083323] transition-colors disabled:opacity-50"
          data-testid="confirm-crop-btn">
          {uploading ? 'Upload...' : 'Valider'}
        </button>
      </div>
    </div>
  );
};

export const ImageUploader = ({ value, onChange, accept = "image/*", label = "Image", allowPdf = false }) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  const acceptTypes = allowPdf ? "image/*,application/pdf" : "image/*";

  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const relativeUrl = res.data.url;
      onChange(relativeUrl);
    } catch (err) {
      console.error('Upload error:', err);
      alert("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setCropSrc(reader.result);
      reader.readAsDataURL(file);
    } else if (file) {
      uploadFile(file);
    }
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setCropSrc(reader.result);
      reader.readAsDataURL(file);
    } else if (file) {
      uploadFile(file);
    }
  };

  const handleCropConfirm = async (blob) => {
    const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
    setCropSrc(null);
    await uploadFile(file);
  };

  const handleSkipCrop = () => {
    // If recropping an existing image, just close the dialog (keep current)
    setCropSrc(null);
  };

  const isPdf = value && value.toLowerCase().endsWith('.pdf');

  return (
    <div>
      <label className="text-sm font-medium text-[#2D241D] mb-2 block">{label}</label>

      {value && (
        <div className="mb-2 flex items-end gap-3">
          <div className="relative inline-block">
            {isPdf ? (
              <div className="w-24 h-24 bg-[#F3EDE5] rounded-xl flex flex-col items-center justify-center">
                <FileText className="w-8 h-8 text-[#E16833]" />
                <span className="text-[10px] text-[#6E5E52] mt-1">PDF</span>
              </div>
            ) : (
              <img src={value.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value} alt="preview" className="w-24 h-24 object-cover rounded-xl"
                onError={(e) => { e.target.style.display = 'none'; }} />
            )}
            <button onClick={() => onChange('')} type="button"
              className="absolute -top-2 -right-2 w-5 h-5 bg-[#E63946] rounded-full flex items-center justify-center text-white"
              data-testid="remove-image-btn">
              <X className="w-3 h-3" />
            </button>
          </div>
          {!isPdf && (
            <button type="button" onClick={async () => {
              try {
                // Fetch existing image as blob to avoid CORS canvas tainting
                const imgUrl = value.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;
                const res = await fetch(imgUrl);
                const blob = await res.blob();
                const blobUrl = URL.createObjectURL(blob);
                setCropSrc(blobUrl);
              } catch {
                const imgUrl = value.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;
                setCropSrc(imgUrl);
              }
            }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#F3EDE5] text-xs font-medium text-[#2D241D] hover:bg-[#E5DCD3] transition-colors border border-[#E5DCD3]"
              data-testid="recrop-existing-btn">
              <Crop className="w-3.5 h-3.5 text-[#D4A853]" /> Recadrer
            </button>
          )}
        </div>
      )}

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors cursor-pointer
          ${dragOver ? 'border-[#E16833] bg-[#FFF3E0]' : 'border-[#E5DCD3] hover:border-[#D4A853]'}
          ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        onClick={() => fileInputRef.current?.click()}
        data-testid="image-drop-zone"
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-1 py-1">
            <Loader2 className="w-6 h-6 text-[#E16833] animate-spin" />
            <span className="text-xs text-[#6E5E52]">Upload en cours...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 py-1">
            <Upload className="w-6 h-6 text-[#6E5E52]" />
            <span className="text-xs text-[#6E5E52]">
              Glissez une {allowPdf ? 'image ou PDF' : 'image'} ici ou cliquez
            </span>
            <span className="text-[10px] text-[#D4A853] flex items-center gap-1">
              <Crop className="w-3 h-3" /> Recadrage interactif
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-2">
        <button type="button" onClick={() => cameraInputRef.current?.click()}
          className="flex items-center gap-1 text-xs text-[#E16833] hover:underline" data-testid="camera-btn">
          <Camera className="w-4 h-4" /> Photo
        </button>
      </div>

      <input ref={fileInputRef} type="file" accept={acceptTypes} className="hidden"
        onChange={handleFileSelect} data-testid="file-input" />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={handleFileSelect} data-testid="camera-input" />

      {/* Pan & Zoom Crop Dialog */}
      <Dialog open={!!cropSrc} onOpenChange={(open) => { if (!open) setCropSrc(null); }}>
        <DialogContent className="max-w-md bg-white rounded-3xl p-0 overflow-hidden admin-theme">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="text-[#2D241D] flex items-center gap-2">
              <Crop className="w-5 h-5 text-[#D4A853]" /> Cadrer la photo
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            {cropSrc && (
              <PanZoomCropper
                src={cropSrc}
                onConfirm={handleCropConfirm}
                onSkip={handleSkipCrop}
                uploading={uploading}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageUploader;
